import { useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { ItemDetail, Member, RequestOut, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { spaceApi } from "../../lib/paths.ts";
import { useAction } from "../../lib/resource.ts";
import { canEdit } from "../../lib/roles.ts";
import { me } from "../../lib/state.ts";
import { titleOf } from "../../lib/titles.ts";
import { toast } from "../../lib/toast.ts";
import { Person } from "../../ui/Avatar.tsx";
import { Button } from "../../ui/Button.tsx";
import { Dialog, DialogActions } from "../../ui/Dialog.tsx";
import { Field, Form, Select, Textarea } from "../../ui/form.tsx";
import { RequestReviewIcon } from "../../ui/icons.tsx";
import { Section } from "../../ui/misc.tsx";
import { ICON_ONLY_NARROW } from "../../ui/styles.ts";

/**
 * Asks a member (human or agent) for a review, with an optional note on what to look at: of one item (its page) or
 * of a selection (the items list's). `send` makes the request(s).
 */
export function RequestForm(p: {
  members: readonly Member[] | undefined;
  send: (reviewer: string, note: string | null) => Promise<void>;
  onCancel: () => void;
}) {
  const [reviewer, setReviewer] = useState("");
  const [note, setNote] = useState("");
  const [send, busy] = useAction(() => p.send(reviewer, note.trim() || null));
  const others = (p.members ?? []).filter((m) => m.id !== me.value?.id);
  return (
    <Form class="flex flex-col gap-4" onSubmit={send}>
      <Field label={t("requests.reviewer")}>
        <Select
          value={reviewer}
          required={true}
          onValue={setReviewer}
          options={[{ value: "", label: t("requests.choose") }, ...others.map((m) => ({ value: m.id, label: m.name }))]}
        />
      </Field>
      <Field label={t("requests.note")} hint={t("requests.noteHint")}>
        <Textarea value={note} onValue={setNote} />
      </Field>
      <DialogActions>
        <Button answer="cancel" onClick={p.onCancel}>
          {t("common.cancel")}
        </Button>
        <Button answer="confirm" variant="primary" submit={true} busy={busy}>
          {t("requests.send")}
        </Button>
      </DialogActions>
    </Form>
  );
}

/** "Request review": a button opening the form in a dialog. */
export function RequestButton(p: {
  space: SpaceOut;
  item: ItemDetail;
  members: readonly Member[] | undefined;
  onDone: () => void;
  /** Its button's own classes (hidden on narrow screens, where a menu opens it: `opener`). */
  class?: string;
  /** Set to what opens its dialog, for opening it from elsewhere (a menu). */
  opener?: { current: (() => void) | null };
}) {
  const [open, setOpen] = useState(false);
  if (p.opener) {
    p.opener.current = () => setOpen(true);
  }
  return (
    <>
      {/* Its icon on a narrow screen (by it alone there, as lib/barLabels.ts says); words alone on a wide one, an icon
          of our own being learned (docs/ICONS.md). */}
      <Button class={`${p.class ?? ""} ${ICON_ONLY_NARROW}`} onClick={() => setOpen(true)}>
        <RequestReviewIcon />
        {t("requests.button")}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} title={t("requests.dialogTitle", { title: titleOf(p.item) })}>
        <RequestForm
          members={p.members}
          onCancel={() => setOpen(false)}
          send={async (reviewer, note) => {
            const r = await api.post<RequestOut>(spaceApi(p.space, `/items/${p.item.id}/requests`), { reviewer, note });
            toast(t("requests.sent", { name: r.reviewer_name ?? r.reviewer }));
            setOpen(false);
            p.onDone();
          }}
        />
      </Dialog>
    </>
  );
}

/** Who's been asked to review the item and why; editors can withdraw a request. */
export function Requests({ space, item, onChange }: { space: SpaceOut; item: ItemDetail; onChange: () => void }) {
  const [withdraw, busy] = useAction(async (r: RequestOut) => {
    await api.del(spaceApi(space, `/requests/${r.id}`));
    toast(t("requests.withdrawn"));
    onChange();
  });
  if (item.requests.length === 0) {
    return null;
  }
  return (
    <Section title={t("requests.title")}>
      <ul class="flex flex-col gap-2">
        {item.requests.map((r) => {
          const name = r.reviewer_name ?? r.reviewer;
          return (
            <li key={r.id} class="rounded-md border border-zinc-200 bg-white p-3 text-sm">
              <span class="flex flex-wrap items-center gap-2">
                <span class="font-medium text-zinc-900">
                  <Person id={r.reviewer} name={name} />
                </span>
                <span class="text-xs text-zinc-500">
                  {t("requests.by", { name: r.requested_by_name ?? r.requested_by })}
                </span>
                {canEdit(space) ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    class="ml-auto"
                    disabled={busy}
                    aria-label={t("requests.withdrawOf", { name })}
                    onClick={() => withdraw(r)}
                  >
                    {t("requests.withdraw")}
                  </Button>
                ) : null}
              </span>
              {r.note ? (
                <p class="mt-1 whitespace-pre-line text-zinc-700" data-selectable="">
                  {r.note}
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>
    </Section>
  );
}
